import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import SortableTableFilterPopover, {
  applyColumnFilter,
  buildColumnOptions,
  getColumnFilterType,
  getRawCellValue,
  isFilterActive,
} from './SortableTableFilters';

// Debounce hook for global search.
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Convert data to CSV (Excel-friendly by default).
const convertToCSV = (data, columns, options = {}) => {
  if (!data || data.length === 0) return '';

  const {
    delimiter = ';',
    includeUtf8Bom = true,
    lineBreak = '\r\n'
  } = options;

  // CSV headers.
  const headers = columns.map(col => col.label).join(delimiter);

  // CSV rows.
  const rows = data.map(item => {
    return columns.map(column => {
      // Use the raw value for exports.
      let value = getRawCellValue(item, column);

      // Convert to string and escape quotes
      if (value === null || value === undefined) value = '';
      value = String(value);

      // If the value contains delimiter, quotes, or line breaks, wrap in quotes (RFC 4180)
      if (value.includes(delimiter) || value.includes('"') || value.includes('\n') || value.includes('\r')) {
        // Escape quotes by doubling them.
        value = value.replace(/"/g, '""');
        value = `"${value}"`;
      }

      return value;
    }).join(delimiter);
  }).join(lineBreak);

  const bom = includeUtf8Bom ? '\uFEFF' : '';
  return `${bom}${headers}${lineBreak}${rows}`;
};

// Trigger a browser download.
const downloadCSV = (csvContent, filename = 'export.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const SortableTable = ({
  data,
  columns,
  emptyMessage,
  countLabel,
  disableFilters = false,
  initialSortBy = null,
  exportable = false,
  exportFilename = 'export.csv',
  exportRenamable = false,
  onRowClick = null,
  fullHeight = false,
  className = null,
  globalSearch = false,
  withSum = false,
  hideCountLabel = false,
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange = null,
  getRowKey = null
}) => {
  const [sortConfig, setSortConfig] = useState(
    initialSortBy || {
      key: columns && columns.length > 0 ? columns[0].key : null,
      direction: 'ascending'
    }
  );
  const [filters, setFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [filterAnchorRect, setFilterAnchorRect] = useState(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [customExportFilename, setCustomExportFilename] = useState('');
  const filterSelectRef = useRef(null);
  const filterAnchorElementRef = useRef(null);
  const exportInputRef = useRef(null);
  const selectedRowKeySet = useMemo(() => new Set((selectedRowKeys || []).map(String)), [selectedRowKeys]);

  // Debounce the global search term.
  const debouncedGlobalSearchTerm = useDebounce(globalSearchTerm, 300);

  // Close the filter when clicking outside.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterSelectRef.current && !filterSelectRef.current.contains(event.target)) {
        setActiveFilterColumn(null);
        setFilterAnchorRect(null);
        filterAnchorElementRef.current = null;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!activeFilterColumn) return;

    const updateFilterAnchorRect = () => {
      const anchorElement = filterAnchorElementRef.current;
      if (!anchorElement || !document.body.contains(anchorElement)) {
        setActiveFilterColumn(null);
        setFilterAnchorRect(null);
        filterAnchorElementRef.current = null;
        return;
      }

      setFilterAnchorRect(anchorElement.getBoundingClientRect());
    };

    window.addEventListener('resize', updateFilterAnchorRect);
    window.addEventListener('scroll', updateFilterAnchorRect, true);

    return () => {
      window.removeEventListener('resize', updateFilterAnchorRect);
      window.removeEventListener('scroll', updateFilterAnchorRect, true);
    };
  }, [activeFilterColumn]);

  // Auto-focus the export input when the modal opens.
  useEffect(() => {
    if (isExportModalOpen && exportInputRef.current) {
      setTimeout(() => {
        if (exportInputRef.current) {
          exportInputRef.current.focus();
          exportInputRef.current.select();
        }
      }, 100);
    }
  }, [isExportModalOpen]);

  const columnFilterTypes = useMemo(() => {
    const types = {};

    columns.forEach(column => {
      if (column.filterable !== false) {
        types[column.key] = getColumnFilterType(column, data);
      }
    });

    return types;
  }, [columns, data]);

  // Generate filter options from data filtered by every other active column.
  const dynamicColumnOptions = useMemo(() => {
    const options = {};

    columns.forEach(column => {
      if (column.filterable !== false) {
        let dataForThisColumn = [...data];

        Object.keys(filters).forEach(key => {
          if (key !== column.key) {
            const filter = filters[key];
            const filterColumn = columns.find(col => col.key === key);
            if (filterColumn && isFilterActive(filter)) {
              dataForThisColumn = dataForThisColumn.filter(item =>
                applyColumnFilter(item, filterColumn, filter, columnFilterTypes[key] || 'text')
              );
            }
          }
        });

        options[column.key] = buildColumnOptions(dataForThisColumn, column);
      }
    });

    return options;
  }, [data, columns, filters, columnFilterTypes]);

  // Handle global search.
  const handleGlobalSearch = (event) => {
    setGlobalSearchTerm(event.target.value);
  };

  // Clear global search.
  const clearGlobalSearch = () => {
    setGlobalSearchTerm('');
  };

  // Handle sorting.
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Toggle filter dropdown.
  const toggleFilter = (columnKey, anchorElement) => {
    if (activeFilterColumn === columnKey) {
      setActiveFilterColumn(null);
      setFilterAnchorRect(null);
      filterAnchorElementRef.current = null;
      return;
    }

    filterAnchorElementRef.current = anchorElement;
    setActiveFilterColumn(columnKey);
    setFilterAnchorRect(anchorElement.getBoundingClientRect());
  };

  const applyFilter = (columnKey, filter) => {
    setFilters({
      ...filters,
      [columnKey]: filter
    });
  };

  const clearFilter = (columnKey) => {
    const newFilters = { ...filters };
    delete newFilters[columnKey];
    setFilters(newFilters);
  };

  // Filter and sort data.
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...data];

    // Apply column filters.
    Object.keys(filters).forEach(key => {
      const filter = filters[key];
      const column = columns.find(col => col.key === key);
      if (column && isFilterActive(filter)) {
        filteredData = filteredData.filter(item => {
          return applyColumnFilter(item, column, filter, columnFilterTypes[key] || 'text');
        });
      }
    });

    // Apply debounced global search.
    if (globalSearch && debouncedGlobalSearchTerm.trim().length > 0) {
      const searchTerm = debouncedGlobalSearchTerm.toLowerCase().trim();

      filteredData = filteredData.filter(item => {
        // Search in all columns.
        return columns.some(column => {
          let searchValue;

          // Get the value to search in.
          if (column.render) {
            searchValue = getRawCellValue(item, column);
          } else {
            searchValue = item[column.key];
          }

          if (searchValue !== null && searchValue !== undefined) {
            return String(searchValue).toLowerCase().includes(searchTerm);
          }
          return false;
        });
      });
    }

    // Sort filtered data.
    return filteredData.sort((a, b) => {
      // Find column for custom sorting.
      const column = columns.find(col => col.key === sortConfig.key);

      // Use custom sort function if provided.
      if (column && column.sortFunction)
        return column.sortFunction(a, b, sortConfig.direction);

      // Get values to compare using raw values.
      let aValue = column && column.render ?
        getRawCellValue(a, column) :
        a[sortConfig.key];

      let bValue = column && column.render ?
        getRawCellValue(b, column) :
        b[sortConfig.key];

      // Handle undefined or null values.
      if (aValue === undefined || aValue === null) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (bValue === undefined || bValue === null) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }

      // Attempt to convert to numbers for numeric comparison.
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      // If both values can be converted to numbers, use numeric comparison.
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'ascending' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise, convert to strings and compare.
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, columns, filters, columnFilterTypes, globalSearch, debouncedGlobalSearchTerm]);

  // Build numeric summaries for columns when requested.
  const summaryByColumnKey = useMemo(() => {
    if (!withSum) return {};

    const summaries = {};

    columns.forEach((column) => {
      // Sample up to 5 non-null raw values to detect numeric columns.
      const samples = [];
      for (let i = 0; i < filteredAndSortedData.length && samples.length < 5; i++) {
        const v = getRawCellValue(filteredAndSortedData[i], column);
        if (v !== undefined && v !== null && v !== '') samples.push(v);
      }

      if (samples.length === 0) {
        summaries[column.key] = { isNumeric: false };
        return;
      }

      const numericSamples = samples
        .map(v => (typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'))))
        .filter(n => Number.isFinite(n));

      // Heuristic: if at least 80% of samples are numeric, treat as numeric.
      const isNumeric = samples.length < 3
        ? numericSamples.length === samples.length
        : (numericSamples.length / samples.length) >= 0.8;

      if (!isNumeric) {
        summaries[column.key] = { isNumeric: false };
        return;
      }

      // Determine integer-only formatting based on samples.
      const isIntegerOnly = numericSamples.every(n => Number.isInteger(n));

      // Compute sum/avg over all filtered rows.
      let sum = 0;
      let count = 0;
      for (let i = 0; i < filteredAndSortedData.length; i++) {
        const v = getRawCellValue(filteredAndSortedData[i], column);
        const n = typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'));
        if (Number.isFinite(n)) {
          sum += n;
          count += 1;
        }
      }

      const avg = count > 0 ? (sum / count) : 0;

      summaries[column.key] = {
        isNumeric: true,
        isIntegerOnly,
        sum,
        avg
      };
    });

    return summaries;
  }, [withSum, columns, filteredAndSortedData]);

  const hasActiveFilters = useMemo(() => (
    Object.values(filters).some(filter => isFilterActive(filter))
  ), [filters]);

  // Generate sort indicators.
  const getSortDirectionIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.58L18 10z" />
        </svg>
      );
    }

    return sortConfig.direction === 'ascending' ? (
      <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7 14l5-5 5 5H7z" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7 10l5 5 5-5H7z" />
      </svg>
    );
  };

  // Generate filter indicator.
  const getFilterIcon = (columnKey) => {
    if (disableFilters) return null;

    const isFiltered = isFilterActive(filters[columnKey]);

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleFilter(columnKey, e.currentTarget);
        }}
        className={`inline-flex items-center justify-center w-6 h-6 ml-2 rounded-md transition-colors ${isFiltered ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/40' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800'}`}
        style={{ minWidth: '16px', minHeight: '16px' }}
        aria-label="Filtrer la colonne"
      >
        <svg
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
        >
          <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"></path>
        </svg>
      </button>
    );
  };

  const getFilterPopoverStyle = () => {
    if (!filterAnchorRect) return {};

    const margin = 12;
    const preferredWidth = 320;
    const belowSpace = window.innerHeight - filterAnchorRect.bottom - margin;
    const aboveSpace = filterAnchorRect.top - margin;
    const openAbove = belowSpace < 360 && aboveSpace > belowSpace;
    const left = Math.min(
      Math.max(filterAnchorRect.right - preferredWidth, margin),
      window.innerWidth - preferredWidth - margin
    );

    return {
      position: 'fixed',
      left,
      top: openAbove ? undefined : filterAnchorRect.bottom + 8,
      bottom: openAbove ? window.innerHeight - filterAnchorRect.top + 8 : undefined,
      maxHeight: Math.max(openAbove ? aboveSpace - 8 : belowSpace - 8, 260),
      zIndex: 9999,
    };
  };

  // Open the export modal or export directly.
  const handleExport = () => {
    if (exportRenamable) {
      setCustomExportFilename(exportFilename);
      setIsExportModalOpen(true);
    } else {
      const csvContent = convertToCSV(filteredAndSortedData, columns);
      downloadCSV(csvContent, exportFilename);
    }
  };

  // Confirm export with the custom filename.
  const handleConfirmExport = () => {
    const finalFilename = customExportFilename.trim() || exportFilename;
    const csvContent = convertToCSV(filteredAndSortedData, columns);
    downloadCSV(csvContent, finalFilename);
    setIsExportModalOpen(false);
  };

  // Handle export form submission.
  const handleExportFormSubmit = (e) => {
    e.preventDefault();
    handleConfirmExport();
  };

  const getSelectableRowKey = (item, index) => String(getRowKey ? getRowKey(item) : index);

  const handleRowSelectionToggle = (item, index) => {
    if (!onSelectionChange) return;

    const rowKey = getSelectableRowKey(item, index);
    const nextSelection = selectedRowKeySet.has(rowKey)
      ? selectedRowKeys.filter((key) => String(key) !== rowKey)
      : [...selectedRowKeys, rowKey];

    onSelectionChange(nextSelection);
  };

  const handleVisibleRowsSelectionToggle = () => {
    if (!onSelectionChange) return;

    const visibleKeys = filteredAndSortedData.map((item, index) => getSelectableRowKey(item, index));
    const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selectedRowKeySet.has(key));
    const nextSelection = allVisibleSelected
      ? selectedRowKeys.filter((key) => !visibleKeys.includes(String(key)))
      : Array.from(new Set([...selectedRowKeys.map(String), ...visibleKeys]));

    onSelectionChange(nextSelection);
  };

  // Close the export modal.
  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setCustomExportFilename('');
  };

  // Determine the container class based on fullHeight prop
  const containerClass = className || (fullHeight
    ? 'flex flex-col h-full relative'
    : 'flex flex-col h-full max-h-[500px] relative');

  return (
    <div className={containerClass}>
      {/* Global Search Bar */}
      {globalSearch && (
        <div className="flex justify-end mb-4">
          <div className="relative w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Rechercher dans le tableau..."
              value={globalSearchTerm}
              onChange={handleGlobalSearch}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            {globalSearchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={clearGlobalSearch}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Effacer la recherche"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="overflow-auto flex-grow rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800">
        {data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
            <thead className="bg-gray-50/90 dark:bg-gray-900 sticky top-0 z-10 backdrop-blur">
              <tr>
                {selectable && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={filteredAndSortedData.length > 0 && filteredAndSortedData.every((item, index) => selectedRowKeySet.has(getSelectableRowKey(item, index)))}
                      onChange={handleVisibleRowsSelectionToggle}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                  >
                    <div className="flex items-center relative">
                      <div
                        onClick={() => requestSort(column.key)}
                        className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 select-none"
                      >
                        <span>{column.label}</span>
                        {getSortDirectionIcon(column.key)}
                      </div>

                      {column.filterable !== false && !disableFilters && (
                        <>
                          {getFilterIcon(column.key)}

                          {activeFilterColumn === column.key && filterAnchorRect && createPortal(
                            <div
                              ref={filterSelectRef}
                              className="bg-white dark:bg-gray-800 shadow-2xl rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                              style={getFilterPopoverStyle()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SortableTableFilterPopover
                                column={column}
                                filterType={columnFilterTypes[column.key] || 'text'}
                                options={dynamicColumnOptions[column.key] || []}
                                filter={filters[column.key]}
                                onApply={(filter) => applyFilter(column.key, filter)}
                                onClear={() => clearFilter(column.key)}
                                onClose={() => {
                                  setActiveFilterColumn(null);
                                  setFilterAnchorRect(null);
                                  filterAnchorElementRef.current = null;
                                }}
                              />
                            </div>,
                            document.body
                          )}
                        </>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              {withSum && (
                <tr>
                  {selectable && (
                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap" />
                  )}
                  {columns.map((column) => {
                    const s = summaryByColumnKey[column.key];
                    let content = '';
                    if (s && s.isNumeric) {
                      const format = (n) => s.isIntegerOnly ? Math.round(n).toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      content = `Σ ${format(s.sum)}  •  Avg ${format(s.avg)}`;
                    }
                    return (
                      <th
                        key={`sum-${column.key}`}
                        className="px-4 py-2 text-left text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap"
                      >
                        {content}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
              {filteredAndSortedData.map((item, index) => {
                if (item.rawValue && typeof item.rawValue === 'function')
                  item.rawValue = item.rawValue(item);

                const rowKey = getSelectableRowKey(item, index);

                return <tr key={rowKey} className={`transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-700/60 ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick && onRowClick(item)}>
                  {selectable && (
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 text-left">
                      <input
                        type="checkbox"
                        checked={selectedRowKeySet.has(rowKey)}
                        onChange={() => handleRowSelectionToggle(item, index)}
                        onClick={(event) => event.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={`${rowKey}-${column.key}`} className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 text-left align-middle">
                      {column.render ? column.render(item) : item[column.key]}
                    </td>
                  ))}
                </tr>
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 flex-grow flex items-center justify-center">
            <div>
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-left">{emptyMessage || "Aucune donnée disponible"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Fixed information bar. */}
      {data.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-800/95 border border-t-0 border-gray-200 dark:border-gray-800 rounded-b-xl py-2 px-4 text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center backdrop-blur">
          <div className="flex items-center gap-2">
            {!hideCountLabel && <p>{countLabel || `Total: ${filteredAndSortedData.length} éléments`}</p>}

            {exportable && filteredAndSortedData.length > 0 && (
              <button
                onClick={handleExport}
                className="ml-3 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 dark:text-green-300 dark:bg-green-900/30 dark:hover:bg-green-900/50"
                title="Exporter en CSV"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {globalSearch && debouncedGlobalSearchTerm && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Recherche: "<span className="font-medium text-gray-700 dark:text-gray-300">{debouncedGlobalSearchTerm}</span>"
                {globalSearchTerm !== debouncedGlobalSearchTerm && (
                  <span className="ml-1 text-blue-500 dark:text-blue-400">...</span>
                )}
              </div>
            )}

            {!disableFilters && hasActiveFilters && (
              <button
                onClick={() => setFilters({})}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
                Effacer tous les filtres
              </button>
            )}
          </div>
        </div>
      )}

      {data.length > 0 && filteredAndSortedData.length === 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-2 px-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          <p>
            Aucun résultat ne correspond aux {globalSearch && debouncedGlobalSearchTerm ? 'critères de recherche' : 'filtres appliqués'}
          </p>
          <div className="mt-2 flex justify-center gap-4">
            {globalSearch && (globalSearchTerm || debouncedGlobalSearchTerm) && (
              <button
                onClick={clearGlobalSearch}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Effacer la recherche
              </button>
            )}
            {hasActiveFilters && (
              <button
                onClick={() => setFilters({})}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal d'export avec nom personnalisé */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-96 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Nommer le fichier d'export
            </h3>

            <form onSubmit={handleExportFormSubmit}>
              <div className="mb-4">
                <label htmlFor="export-filename" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nom du fichier CSV
                </label>
                <input
                  ref={exportInputRef}
                  id="export-filename"
                  type="text"
                  value={customExportFilename}
                  onChange={(e) => setCustomExportFilename(e.target.value)}
                  placeholder={exportFilename}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Laissez vide pour utiliser le nom par défaut
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseExportModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Exporter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortableTable;